import moment from "moment";

import {FactorySession} from "types";

export const FAMILY_OPTIONS = [
    {value: 'p23', label: 'Pavlok 2/3'},
    {value: 'scmax', label: 'ShockClock Max'},
]

export const OVERALL_OPTIONS = [
    {value: 'pass', label: 'Pass'},
    {value: 'fail', label: 'Fail'},
    {value: 'incomplete', label: 'Incomplete'},
    {value: 'error', label: 'Error'},
]

// Same ids as the device tester failure bins
export const FAILURE_BIN_LABELS: { [key: string]: string } = {
    charging: 'Charging',
    bluetooth: 'Bluetooth',
    firmware: 'Firmware',
    led: 'LED',
    vibe: 'Vibe',
    beep: 'Beep',
    zap: 'Zap',
    button: 'Button',
    display: 'Display',
    flashlight: 'Flashlight',
    ppg: 'PPG',
    cosmetic: 'Cosmetic',
    other: 'Other / Multiple',
}

const STATUS_COLORS: { [key: string]: string } = {
    pass: 'green',
    fail: 'red',
    error: 'volcano',
    incomplete: 'orange',
    skipped: 'default',
    pending: 'default',
    running: 'blue',
}

export const getStatusColor = (status?: string | null): string => STATUS_COLORS[status || ''] || 'default'

export const getFamilyLabel = (family?: string | null): string =>
    FAMILY_OPTIONS.find(option => option.value === family)?.label || family || ''

export const getFailureBinLabel = (bin?: string | null): string => (bin && FAILURE_BIN_LABELS[bin]) || bin || ''

export const getWorkerLabel = (session: FactorySession): string =>
    session.worker?.email || session.worker?.username || `#${session.worker?.id}`

export const getSessionDate = (session: FactorySession): string => session.startedAt || session.createdAt

export const formatDate = (value?: string | null): string => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : ''

// ----- CSV -----
const escapeCsvValue = (value: unknown): string => {
    if (value === null || value === undefined) return ''
    const str = String(value)
    return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

// One row per session: summary columns, then one status column per metric id seen
export const sessionsToCsv = (sessions: FactorySession[]): string => {
    const metricIds: string[] = []
    sessions.forEach(session => session.metrics?.forEach(metric => {
        if (metric?.metricId && !metricIds.includes(metric.metricId)) metricIds.push(metric.metricId)
    }))

    const header = [
        'session_id', 'date', 'worker_id', 'worker', 'family', 'serial', 'bluetooth_id', 'device_name',
        'firmware_version', 'hardware_version', 'battery_percent', 'battery_mv', 'overall', 'failure_bin',
        'failure_note', ...metricIds,
    ]
    const rows = sessions.map(session => {
        const statusByMetric = new Map(session.metrics?.map(metric => [metric.metricId, metric.status]))
        return [
            session.id,
            formatDate(getSessionDate(session)),
            session.worker?.id,
            getWorkerLabel(session),
            session.family,
            session.device?.serial,
            session.device?.bluetoothId,
            session.device?.name,
            session.device?.firmwareVersion,
            session.device?.hardwareVersion,
            session.device?.batteryPercent,
            session.device?.batteryMv,
            session.overall,
            session.failureBin,
            session.failureNote,
            ...metricIds.map(metricId => statusByMetric.get(metricId)),
        ]
    })

    return [header, ...rows].map(row => row.map(escapeCsvValue).join(',')).join('\n')
}

export const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}
